import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Leaf, CheckCircle2, Circle } from "lucide-react";
import { GeoUpload, DocUpload, CustodyForm } from "@/components/eudr/CaseWizard";

const pub = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/eudr/supplier` });

export default function EudrSupplier() {
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const load = () => pub.get(`/${token}`).then((r) => setC(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Link not available"));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } };
  if (err) return <div data-testid="supplier-error" className="p-10 text-center text-sm text-[#6a6d70]">{err}</div>;
  if (!c) return null;
  const card = "rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm";
  return (
    <div data-testid="eudr-supplier-portal" className="min-h-screen bg-[#f7f8fa] px-4 py-8">
      <div className="mx-auto max-w-3xl space-y-4">
        <div className={card}>
          <div className="flex items-center gap-2 text-[#1a7f37]"><Leaf /> <span className="text-xs font-semibold uppercase tracking-widest">EP Group · EUDR supplier upload</span></div>
          <h1 className="mt-1 text-xl font-bold text-[#161d23]">{c.supplier}: evidence for batch {c.batch_no}</h1>
          <p className="text-sm text-[#6a6d70]">Case {c.case_no} · PO {c.po_no} · {c.material_name} · origin {c.country}. Upload the items below; EP Group reviews everything before the Due Diligence Statement is filed. Link valid until {c.expires?.slice(0, 10)}.</p>
          <a data-testid="supplier-guide" href="/guides/EUDR_Supplier_Guide.pdf" target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-semibold text-[#1a7f37] underline">How to prepare your map file and documents (2-page guide)</a>
        </div>
        <div className={card}>
          <div className="mb-2 font-semibold">1. Geolocation of the plots of land</div>
          <p className="mb-2 text-xs text-[#6a6d70]">GeoJSON (WGS84), 6+ decimal places. Plots over 4 ha must be polygons; points need an <span className="font-mono">area_ha</span> property.</p>
          <div className="flex items-center gap-3 text-sm"><GeoUpload testid="supplier-geo-upload" onGeo={(gj) => act(() => pub.put(`/${token}/geo`, { geojson: gj }), "Geolocation received")} /> <span data-testid="supplier-plot-count">{c.plots} plot(s) received</span></div>
          {c.geo_issues.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-amber-700">{c.geo_issues.map((i) => <li key={i}>{i}</li>)}</ul>}
        </div>
        <div className={card}>
          <div className="mb-2 font-semibold">2. Supporting documents</div>
          {c.docs.map((d) => (
            <div key={d.kind} className="flex items-center gap-2 border-b border-[#f0f1f3] py-2 text-sm last:border-0">
              {d.uploaded ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Circle size={16} className="text-[#9aa0a6]" />}
              <span className="flex-1">{d.label}</span>
              <DocUpload kind={d.kind} done={d.uploaded} onFile={(kind, f) => act(() => { const fd = new FormData(); fd.append("kind", kind); fd.append("file", f); return pub.post(`/${token}/documents`, fd); }, "Document received")} />
            </div>
          ))}
        </div>
        <div className={card}>
          <div className="mb-1 font-semibold">3. Chain of custody (your stages)</div>
          {c.custody.map((r) => <div key={r.seq} className="text-xs text-[#32363a]">{r.seq}. {r.stage} · {r.actor} · {r.date} · {r.qty_kg} kg</div>)}
          <CustodyForm stages={c.stages} batch={c.batch_no} onAdd={(b) => act(() => pub.post(`/${token}/custody`, b), "Handover recorded")} />
        </div>
      </div>
    </div>
  );
}
